import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteCard from "@/components/feature/SiteCard";
import Pagination from "@/components/feature/Pagination";
import SortSelect from "@/components/feature/SortSelect";
import EmptyState from "@/components/feature/EmptyState";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getCategoryById, getSitesByCategory, sortSites } from "@/utils/site";

const PAGE_SIZE = 12;
const sortOptions = [
  { value: "default", label: "综合排序" },
  { value: "visits", label: "按访问量" },
  { value: "updated", label: "按更新时间" },
  { value: "name", label: "按字母顺序" },
];

export default function Category() {
  const { categoryId = "" } = useParams();
  const category = getCategoryById(categoryId);

  const [sort, setSort] = useState("default");
  const [tag, setTag] = useState("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setSort("default");
    setTag("all");
    setPage(1);
  }, [categoryId]);

  const categorySites = useMemo(() => getSitesByCategory(categoryId), [categoryId]);

  const tagOptions = useMemo(() => {
    const set = new Set<string>();
    categorySites.forEach((s) => s.tags.forEach((t) => set.add(t)));
    return ["all", ...Array.from(set)];
  }, [categorySites]);

  const filtered = useMemo(() => {
    const base = tag === "all" ? categorySites : categorySites.filter((s) => s.tags.includes(tag));
    return sortSites(base, sort);
  }, [categorySites, tag, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const paged = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  usePageMeta({
    title: category ? `${category.name}导航 - 网址导航分类` : "分类不存在 - 网址导航",
    description: category
      ? `${category.desc}目前共收录 ${categorySites.length} 个优质网站，支持按访问量与更新时间排序。`
      : "该分类不存在或已调整，请返回首页浏览其他热门分类。",
    keywords: category ? `${category.name}导航,${category.name}网站,网址导航,热门网站,网站入口` : "网址导航,网站分类",
  });

  if (!category) {
    return (
      <div className="mx-auto max-w-[1280px] px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-folder-warning-line"
          title="该分类暂不可用"
          description="你要查看的分类不存在或已调整，返回首页浏览其他热门分类吧。"
          action={{ label: "返回首页", to: "/" }}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
      <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑导航">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-500">分类</span>
        <i className="ri-arrow-right-s-line"></i>
        <span className="font-medium text-foreground-800">{category.name}</span>
      </nav>

      <div className="mt-5 flex items-start gap-4">
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
          <i className={`${category.icon} text-2xl`}></i>
        </span>
        <div>
          <h1 className="text-2xl font-bold text-foreground-950 md:text-3xl">{category.name}导航</h1>
          <p className="mt-1.5 max-w-3xl text-sm leading-relaxed text-foreground-600">{category.desc}</p>
        </div>
      </div>

      <div className="mt-7 flex flex-wrap gap-2">
        {tagOptions.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => {
              setTag(t);
              setPage(1);
            }}
            className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors ${
              tag === t
                ? "border-primary-500 bg-primary-500 font-medium text-background-50"
                : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
            }`}
          >
            {t === "all" ? "全部" : t}
          </button>
        ))}
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-b border-background-200 pb-4">
        <p className="text-sm text-foreground-600">
          共 <span className="font-semibold text-primary-600">{filtered.length}</span> 个网站
        </p>
        <SortSelect value={sort} onChange={setSort} options={sortOptions} />
      </div>

      {paged.length === 0 ? (
        <div className="mt-8">
          <EmptyState
            title="暂无数据，稍后再来"
            description="当前筛选条件下没有匹配的网站，试试切换其他标签或返回首页。"
            action={{ label: "返回首页", to: "/" }}
          />
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {paged.map((site) => (
            <SiteCard key={site.id} site={site} />
          ))}
        </div>
      )}

      <Pagination page={current} totalPages={totalPages} onChange={setPage} />
    </div>
  );
}